import { z } from 'zod'
import { pageQuerySchema } from '../pagination'
import { isoInstantSchema } from '../validators'
import {
  FEED_DATASETS,
  FEED_PROVIDERS,
  FEED_RUN_STATUSES,
  FEED_RUN_TRIGGERS,
  LICENCE_STATUSES,
  vocab,
} from '../vocab'

/**
 * Feed operations (M10, P-ADM-011 adapted): provider status, run history and
 * manual re-runs. Every provider in the demo is simulated (ARCHITECTURE §2).
 */

/**
 * The fixed error-code taxonomy of a failed run (GAP-TP-012). Messages are
 * generic and never carry provider response bodies.
 */
export const FEED_RUN_ERROR_CODES = vocab(
  [
    'network_error',
    'provider_rate_limit',
    'provider_http_error',
    'invalid_response',
    'response_too_large',
    'timeout',
  ],
  {
    network_error: 'The provider could not be reached.',
    provider_rate_limit: 'The provider rate-limited the request.',
    provider_http_error: 'The provider answered with an error.',
    invalid_response: 'The delivery could not be read.',
    response_too_large: 'The delivery exceeded the size limit.',
    timeout: 'The delivery did not complete in time.',
  },
)
export type FeedRunErrorCode = (typeof FEED_RUN_ERROR_CODES.values)[number]

export const FEED_CADENCES = vocab(['hourly', 'daily'], { hourly: 'Hourly', daily: 'Daily' })
export type FeedCadence = (typeof FEED_CADENCES.values)[number]

/** Derived provider health for the admin cards. */
export const FEED_HEALTH = vocab(['healthy', 'degraded', 'failing', 'late', 'idle', 'disabled'], {
  healthy: 'Healthy',
  degraded: 'Degraded',
  failing: 'Failing',
  late: 'Late',
  idle: 'No runs yet',
  disabled: 'Disabled',
})
export type FeedHealth = (typeof FEED_HEALTH.values)[number]

export const feedRunErrorSchema = z.object({ code: z.string(), message: z.string() })

export const feedRunSchema = z.object({
  id: z.string(),
  provider_id: z.enum(FEED_PROVIDERS.values),
  dataset: z.string(),
  attempt: z.number().int(),
  retry_of_run_id: z.string().nullable(),
  trigger: z.enum(FEED_RUN_TRIGGERS.values),
  requested_by: z.string().nullable(),
  requested_by_name: z.string().nullable(),
  status: z.enum(FEED_RUN_STATUSES.values),
  scheduled_for: z.string().nullable(),
  window_from: z.string().nullable(),
  window_to: z.string().nullable(),
  started_at: z.string(),
  finished_at: z.string().nullable(),
  duration_ms: z.number().int().nullable(),
  /** Null when the run failed: a failure is never "zero results" (GAP-TP-012). */
  returned_count: z.number().int().nullable(),
  imported_count: z.number().int().nullable(),
  reused_count: z.number().int().nullable(),
  rejected_count: z.number().int().nullable(),
  signal_count: z.number().int(),
  /** Tri-state: null when the run failed and emptiness is unknown. */
  empty: z.boolean().nullable(),
  cap_reached: z.boolean(),
  error_code: z.string().nullable(),
  error_label: z.string().nullable(),
  http_status: z.number().int().nullable(),
  errors: z.array(feedRunErrorSchema),
  limitations: z.array(z.string()),
  attribution: z.string().nullable(),
  next_retry_at: z.string().nullable(),
})
export type FeedRun = z.infer<typeof feedRunSchema>

/** Runs per day for the 7-day sparkline/timeline (oldest first). */
export const feedDaySchema = z.object({
  day: z.string(),
  ok: z.number().int(),
  partial: z.number().int(),
  empty: z.number().int(),
  failed: z.number().int(),
  late: z.number().int(),
})
export type FeedDay = z.infer<typeof feedDaySchema>

export const feedProviderStatusSchema = z.object({
  id: z.enum(FEED_PROVIDERS.values),
  name: z.string(),
  simulated: z.boolean(),
  licence_status: z.enum(LICENCE_STATUSES.values),
  /** "Fruuit Screening Engine · Licensed" */
  licence_label: z.string(),
  description: z.string(),
  attribution: z.string(),
  terms_url: z.string(),
  datasets: z.array(z.enum(FEED_DATASETS.values)),
  cadence: z.enum(FEED_CADENCES.values),
  schedule_label: z.string(),
  late_after_minutes: z.number().int(),
  identifier_types: z.array(z.string()),
  enabled: z.boolean(),
  health: z.enum(FEED_HEALTH.values),
  last_run: feedRunSchema.nullable(),
  last_success_at: z.string().nullable(),
  next_due_at: z.string().nullable(),
  runs_24h: z.number().int(),
  runs_7d: z.number().int(),
  /** Share of terminal runs that were ok or empty; null without runs. */
  success_rate_24h: z.number().nullable(),
  success_rate_7d: z.number().nullable(),
  open_alerts: z.number().int(),
  signals_7d: z.number().int(),
  days: z.array(feedDaySchema),
})
export type FeedProviderStatus = z.infer<typeof feedProviderStatusSchema>

export const feedListSchema = z.object({
  generated_at: z.string(),
  providers: z.array(feedProviderStatusSchema),
  disclaimer: z.string(),
})
export type FeedList = z.infer<typeof feedListSchema>

export const feedRunsQuerySchema = pageQuerySchema.extend({
  status: z.enum(FEED_RUN_STATUSES.values).optional(),
})
export type FeedRunsQuery = z.infer<typeof feedRunsQuerySchema>

/**
 * Manual re-run (operator). Without `scheduled_for` the latest due slot is
 * re-fetched; duplicates are reused, never re-imported (GAP-TP-014/-015).
 */
export const feedRunRequestSchema = z.strictObject({
  scheduled_for: isoInstantSchema.optional(),
})
export type FeedRunRequest = z.infer<typeof feedRunRequestSchema>

export const feedRunResultSchema = z.object({
  outcome: z.enum(['ran', 'not_ready', 'skipped']),
  message: z.string(),
  run: feedRunSchema.nullable(),
})
export type FeedRunResult = z.infer<typeof feedRunResultSchema>

export const FEED_DISCLAIMER =
  'Feed records in this demo are fictional and generated from the demo world; no provider data, scores or methodology are used. Feed signals are candidates for human assessment and are never published automatically.'
